export type FileType = 'image' | 'video' | 'audio' | 'document'

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']
const VIDEO_EXTENSIONS = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm']
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'aac', 'm4a', 'opus']

/**
 * Composable unificado para formateo y validación de archivos
 * Consolida funcionalidades de fileHelpers y fileFormatters
 */
export const useFileFormatter = () => {
  /**
   * Formatea el tamaño de un archivo en unidades legibles
   */
  const formatFileSize = (size: string | number | null | undefined): string => {
    if (!size) return ''
    const bytes = typeof size === 'string' ? parseInt(size) : size
    if (isNaN(bytes)) return ''

    if (bytes < 1024) return `${bytes} B`
    const kb = bytes / 1024
    if (kb < 1024) return `${kb.toFixed(1)} KB`
    const mb = kb / 1024
    if (mb < 1024) return `${mb.toFixed(1)} MB`
    const gb = mb / 1024
    return `${gb.toFixed(1)} GB`
  }

  /**
   * Obtiene la extensión de un archivo
   */
  const getFileExtension = (fileName: string): string =>
    fileName.split('.').pop()?.toLowerCase() || ''

  /**
   * Obtiene el nombre del icono según el tipo de archivo
   */
  const getFileIconName = (fileName: string | null | undefined): string => {
    if (!fileName) return 'File'

    const ext = getFileExtension(fileName)

    if (ext === 'pdf') return 'FileText'
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'FileSpreadsheet'
    if (['doc', 'docx'].includes(ext)) return 'FileText'
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'FileArchive'
    if (['js', 'ts', 'json', 'xml', 'html', 'css', 'py', 'java'].includes(ext))
      return 'FileCode'
    if (['txt', 'md', 'log'].includes(ext)) return 'FileType'
    if (IMAGE_EXTENSIONS.includes(ext)) return 'FileImage'
    if (VIDEO_EXTENSIONS.includes(ext)) return 'FileVideo'
    if (AUDIO_EXTENSIONS.includes(ext)) return 'FileAudio'

    return 'File'
  }

  /**
   * Valida que el tamaño del archivo no exceda el límite
   */
  const validateFileSize = (file: File, maxSizeMB: number = 16): boolean => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024
    return file.size <= maxSizeBytes
  }

  /**
   * Determina si un archivo es una imagen
   */
  const isImage = (file: File): boolean => file.type.startsWith('image/')

  /**
   * Determina si un archivo es un video
   */
  const isVideo = (file: File): boolean => file.type.startsWith('video/')

  /**
   * Determina si un archivo es audio
   */
  const isAudio = (file: File): boolean => file.type.startsWith('audio/')

  /**
   * Obtiene el tipo de archivo basado en su extensión
   */
  const getFileType = (file: File): FileType => {
    const extension = getFileExtension(file.name)

    if (IMAGE_EXTENSIONS.includes(extension)) return 'image'
    if (VIDEO_EXTENSIONS.includes(extension)) return 'video'
    if (AUDIO_EXTENSIONS.includes(extension)) return 'audio'

    return 'document'
  }

  /**
   * Convierte un archivo a base64
   */
  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        const base64Data = result.includes(',') ? result.split(',')[1] : result
        resolve(base64Data)
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })

  return {
    // Formatters
    formatFileSize,
    getFileExtension,
    getFileIconName,

    // Validators
    validateFileSize,
    isImage,
    isVideo,
    isAudio,

    // Type detection
    getFileType,

    // Converters
    fileToBase64,
  }
}
